import { MultiAreaChart } from "@shared/components/Charts/MultiAreaChart";
import type { MultiAreaRow } from "@shared/components/Charts/MultiAreaChart";
import type { YaasStatsResponse } from "@shared/types/yaas";
import { formatCurrency, formatCurrencyCompact } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import { graphColorValue } from "@solomon-labs/design/tokens/graph";
import React from "react";

interface TotalSupplySectionProps {
    /** Daily mint and redeem observations. Empty means no history to draw. */
    history: NonNullable<YaasStatsResponse["flowSeries"]>;
}

/**
 * A month of daily observations before this reads as history rather than as a
 * few days dressed up in a chart. Below it the section does not render at all:
 * a heading over an apology is worse than the page simply not making the claim.
 */
const MINIMUM_DAYS = 30;

/**
 * Total Supply: cumulative USDv minted against the supply still outstanding.
 *
 * The gap between the two lines is what has been redeemed. Supply is derived
 * from the flow series rather than observed onchain, so it tracks the mint only
 * while redemption is the sole burn path.
 */
export function TotalSupplySection({
    history
}: TotalSupplySectionProps): React.ReactElement | null {
    const data = React.useMemo(() => supplyChartRows(history), [history]);

    if (data.length <= MINIMUM_DAYS) return null;

    return (
        <div
            data-motion-section
            style={{
                opacity: 0,
                animation: "page-fade-up 360ms ease-out 80ms forwards"
            }}
        >
            <section aria-labelledby="total-supply-heading">
                <SectionHeading>
                    <span id="total-supply-heading">Total Supply</span>
                </SectionHeading>
                <div className="pt-5">
                    <MultiAreaChart
                        inspection="inline"
                        data={data}
                        series={[
                            {
                                key: "minted",
                                name: "Minted",
                                color: graphColorValue("graph-quinary"),
                                slot: "quinary"
                            },
                            {
                                key: "supply",
                                name: "Supply",
                                color: graphColorValue("graph-octonary"),
                                slot: "octonary"
                            }
                        ]}
                        gradientIdPrefix="total-supply"
                        valueFormatter={(value) => formatCurrency(value)}
                        yTickFormatter={formatCurrencyCompact}
                        height={200}
                        showLegend
                    />
                </div>
            </section>
        </div>
    );
}

/**
 * Runs the daily observations into two cumulative series: everything minted to
 * date, and that less everything redeemed. Bucket volumes are per-day, so the
 * running totals are built here. A missing day breaks the line rather than
 * carrying a total across a gap we did not observe.
 */
export function supplyChartRows(
    history: NonNullable<YaasStatsResponse["flowSeries"]>
): MultiAreaRow[] {
    const daily = history
        .filter(
            (point) =>
                Date.parse(point.to) - Date.parse(point.from) === 86400000
        )
        .sort((a, b) => Date.parse(a.from) - Date.parse(b.from));

    const rows: MultiAreaRow[] = [];
    let minted = new BigDecimal(0);
    let redeemed = new BigDecimal(0);

    for (const point of daily) {
        const t = Date.parse(point.from);
        if (rows.length && t - rows[rows.length - 1].t > 86400000) {
            rows.push({ t: t - 86400000 });
        }
        minted = minted.add(new BigDecimal(point.mintedVolumeUi));
        redeemed = redeemed.add(new BigDecimal(point.redeemedVolumeUi));

        // Convert only at the chart boundary; token arithmetic stays decimal.
        rows.push({
            t,
            minted: minted.toNumber(),
            supply: minted.subtract(redeemed).toNumber()
        });
    }

    return rows;
}
